import { defineStore } from "pinia";
import { useHead } from "#imports";

const DEFAULT_BRAND_COLORS = {
  brandFirst: "#2b2b2b",
  brandSecond: "#fc4845",
  brandThird: "#f7f7f7",
  brandDark: "#1a1a1a",
  brandDarkSecond: "#c53532",
  brandLight: "#ffe3e3",
  brandLightSecond: "#dddddd",
  brandBackground: "#f1f1f1",
};

const clampColorValue = (value) => Math.max(0, Math.min(255, Number(value)));

const hexToRgb = (value) => {
  if (!value || typeof value !== "string") return null;
  const normalized = value.trim().replace("#", "");
  if (![3, 6].includes(normalized.length)) return null;

  const fullHex =
    normalized.length === 3
      ? normalized
          .split("")
          .map((char) => char + char)
          .join("")
      : normalized;

  const intValue = parseInt(fullHex, 16);
  const r = (intValue >> 16) & 255;
  const g = (intValue >> 8) & 255;
  const b = intValue & 255;
  return `${r} ${g} ${b}`;
};

const rgbStringToRgb = (value) => {
  if (!value || typeof value !== "string") return null;
  const match = value.trim().match(/^rgba?\(([^)]+)\)$/i);
  if (!match) return null;
  const channels = match[1]
    .split(",")
    .slice(0, 3)
    .map((channel) => clampColorValue(channel.trim()));

  if (
    channels.length < 3 ||
    channels.some((channel) => Number.isNaN(channel))
  ) {
    return null;
  }

  return `${channels[0]} ${channels[1]} ${channels[2]}`;
};

const normalizeColorToRgb = (value) => rgbStringToRgb(value) ?? hexToRgb(value);

const camelToKebab = (value) =>
  value.replace(/([a-z0-9])([A-Z])/g, "$1-$2").toLowerCase();

const applyColorVariables = (variables) => {
  if (!variables.length) return;

  const styleContent = variables
    .map(([name, value]) => `${name}: ${value};`)
    .join("");

  useHead({
    style: [
      {
        key: "site-brand-colors",
        innerHTML: `:root{${styleContent}}`,
      },
    ],
  });
};

const resolveColorValue = (colorSource, key) => {
  if (!colorSource) return undefined;

  const kebabKey = camelToKebab(key);
  const snakeKey = key.replace(/([A-Z])/g, "_$1").toLowerCase();
  
  // Mapping for brand colors based on common Strapi naming and Turkish/English labels
  const mappings = {
    brandFirst: ['brand_first', 'brand-first', 'firstBrandColor', 'birinciMarkaRengi', 'birinci_marka_rengi', 'colorFirst', 'color_first', 'first'],
    brandSecond: ['brand_second', 'brand-second', 'secondBrandColor', 'ikinciMarkaRengi', 'ikinci_marka_rengi', 'colorSecond', 'color_second', 'second'],
    brandThird: ['brand_third', 'brand-third', 'thirdBrandColor', 'ucuncuMarkaRengi', 'ucuncu_marka_rengi', 'colorThird', 'color_third', 'third'],
    brandDark: ['brand_dark', 'brand-dark', 'darkColor', 'koyuRenk', 'koyu_renk', 'colorDark', 'color_dark', 'dark'],
    brandDarkSecond: ['brand_dark_second', 'brand-dark-second', 'secondDarkColor', 'ikinciKoyuRenk', 'ikinci_koyu_renk', 'colorDarkSecond', 'color_dark_second', 'dark_second'],
    brandLight: ['brand_light', 'brand-light', 'lightColor', 'acikRenk', 'acik_renk', 'colorLight', 'color_light', 'light'],
    brandLightSecond: ['brand_light_second', 'brand-light-second', 'secondLightColor', 'ikinciAcikRenk', 'ikinci_acik_renk', 'colorLightSecond', 'color_light_second', 'light_second'],
    brandBackground: ['brand_background', 'brand-background', 'backgroundColor', 'arkaplanRengi', 'arkaplan_rengi', 'bg_color', 'colorBackground', 'color_background', 'background'],
  };

  const candidates = mappings[key] || [];

  for (const candidate of [key, kebabKey, snakeKey, ...candidates]) {
    if (colorSource[candidate] !== undefined && colorSource[candidate] !== null) return colorSource[candidate];
  }
  
  return undefined;
};

export const useLoadingStore = defineStore("loading", {
  state: () => ({
    isLoading: false,
    isNavLoading: false,
  }),
  actions: {
    setLoading(data) {
      this.isLoading = data;
    },
    setNavLoading(data) {
      this.isNavLoading = !!data;
    },
  },
});

export const useLanguagesStore = defineStore("languages", {
  state: () => ({
    languages: [],
  }),
  actions: {
    setLanguages(data) {
      this.languages = data;
    },
  },
});

export const useModalStore = defineStore("modals", {
  state: () => ({
    isQuoteModal: false,
  }),
  actions: {
    triggerModal() {
      this.isQuoteModal = true;
    },
    resetModal() {
      this.isQuoteModal = false;
    },
  },
});

export const useSiteSettingsStore = defineStore("siteSettings", {
  state: () => ({
    phone: "",
    policy: "",
    brandName: "",
    roundCorner: false,
    animateScroll: true,
    formType: "offer",
    instagramToken: "",
    recaptchaToken: "",
    formServices: [],
    shape: {
      heroShape: false,
      footerShape: false,
    },
    logo: "",
    fontFamily: "",
    colors: { ...DEFAULT_BRAND_COLORS },
    brandColorVariables: [], // RGB values for Tailwind
    socialMedia: [],
    footerContact: null,
    footerContactLocale: "",
    fixedMenu: null,
  }),
  actions: {
    setPhone(data) {
      this.phone = data;
    },
    setPolicy(data) {
      this.policy = data;
    },
    setBrandName(data) {
      this.brandName = typeof data === "string" ? data.trim() : "";
    },
    setRoundCorner(data) {
      this.roundCorner = data === true || data === 'true';
    },
    setAnimateScroll(enabled) {
      this.animateScroll = enabled !== false && enabled !== 'false';
    },
    setformType(data) {
      this.formType = data;
    },
    setInstagramToken(data) {
      this.instagramToken = data || "";
    },
    setRecaptchaToken(token) {
      this.recaptchaToken = token || "";
    },
    setFormServices(data = []) {
      const normalizeItem = (item) => {
        if (!item) return null;
        if (typeof item === "string") return item.trim();
        if (typeof item === "object") {
          const attributes = item?.attributes || {};
          const candidate = item?.label || item?.title || item?.name || item?.value || item?.text || item?.service || attributes?.label || attributes?.title || attributes?.name || attributes?.value || attributes?.text || attributes?.service || "";
          return candidate.toString().trim();
        }
        return null;
      };

      const items = Array.isArray(data) ? data : (typeof data === "string" ? data.split(/[\n,]/) : []);
      this.formServices = items
        .map(normalizeItem)
        .filter((item) => typeof item === "string" && item.length > 0);
    },
    setShape(heroShape, footerShape) {
      this.shape.heroShape = heroShape;
      this.shape.footerShape = footerShape;
    },
    setLogo(data) {
      this.logo = data;
    },
    setFontFamily(fontName) {
      this.fontFamily = fontName || "";
      if (fontName) {
        useHead({
          style: [
            {
              key: "site-font-family",
              innerHTML: `:root{--app-font: "${fontName}", sans-serif;}`,
            },
          ],
          htmlAttrs: {
            class: fontName.toLowerCase().includes("quicksand") ? "" : "non-quicksand-font"
          }
        });
      }
    },
    setColors(colorSource = {}) {
      const pickPalette = (source) =>
        source?.colors ||
        source?.colorPalette ||
        source?.siteColors ||
        source?.site_colors ||
        source?.siteRenkleri ||
        source?.site_renkleri ||
        source?.site_setting_colors ||
        source?.themeColors ||
        source?.palette ||
        source?.brandColors ||
        source?.branding ||
        null;
      const unwrapValue = (value) => {
        if (Array.isArray(value)) return value.find(Boolean);
        if (typeof value === "string") {
          try { return JSON.parse(value); } catch (_err) { return value; }
        }
        return value;
      };

      const baseSourceRaw = unwrapValue(colorSource);
      const paletteSource = baseSourceRaw?.colors || 
                            baseSourceRaw?.attributes?.colors || 
                            baseSourceRaw?.data?.attributes?.colors || 
                            baseSourceRaw?.siteSettings?.colors ||
                            baseSourceRaw?.site_settings?.colors ||
                            pickPalette(baseSourceRaw) || 
                            pickPalette(baseSourceRaw?.attributes) || 
                            pickPalette(baseSourceRaw?.data?.attributes) || 
                            baseSourceRaw || {};

      const palette = { ...DEFAULT_BRAND_COLORS };
      const cssVariables = [];

      Object.entries(DEFAULT_BRAND_COLORS).forEach(([key, fallbackColor]) => {
        const candidate = resolveColorValue(paletteSource, key) ?? fallbackColor;
        const normalized = normalizeColorToRgb(candidate) ?? normalizeColorToRgb(fallbackColor);
        palette[key] = candidate || fallbackColor;
        if (normalized) {
          cssVariables.push([`--${camelToKebab(key)}`, normalized]);
        }
      });

      this.colors = palette;
      this.brandColorVariables = cssVariables;
      // applyColorVariables(cssVariables); // Replaced by layout injection
    },
    setSocialMedia(data) {
      this.socialMedia = Array.isArray(data) ? data.filter(Boolean) : [];
    },
    setFooterContact(data, locale = "") {
      const contactEntries = Array.isArray(data?.contact) ? data.contact.filter(Boolean) : [];
      const primaryContact = contactEntries.find((entry) => entry?.address || entry?.phone || entry?.mail) ?? contactEntries[0] ?? {};

      this.footerContact = {
        heading: primaryContact?.heading || data?.title || "",
        address: primaryContact?.address || "",
        phone: primaryContact?.phone || "",
        email: primaryContact?.mail || primaryContact?.email || "",
        whatsapp: primaryContact?.whatsapp || "",
        viber: primaryContact?.viber || "",
        wechat: primaryContact?.wechat || "",
        fax: primaryContact?.fax || "",
        button: primaryContact?.button || null,
      };
      this.footerContactLocale = locale || "";
    },
    setFixedMenu(data) {
      this.fixedMenu = data;
    },
  },
});
